export type ScreenMetrics = {
  cssWidth: number
  cssHeight: number
  availWidth: number
  availHeight: number
  dpr: number
  physicalWidth: number
  physicalHeight: number
  innerWidth: number
  innerHeight: number
  outerWidth: number
  outerHeight: number
  visualScale: number | null
  colorDepth: number
  pixelDepth: number
  orientationType: string
  orientationAngle: number | null
  maxTouchPoints: number
  colorScheme: string
  reducedMotion: boolean
  contrast: string
  colorGamut: string
  hdr: boolean
  aspectRatio: string
  safeArea: { top: number; right: number; bottom: number; left: number }
  isExtended: boolean | null
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a))
  let y = Math.abs(Math.round(b))
  while (y) {
    const t = y
    y = x % y
    x = t
  }
  return x || 1
}

function aspectLabel(w: number, h: number): string {
  const g = gcd(w, h)
  return `${Math.round(w / g)}:${Math.round(h / g)}`
}

function mq(query: string): boolean {
  return window.matchMedia(query).matches
}

function readSafeArea(): ScreenMetrics['safeArea'] {
  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;visibility:hidden;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
  document.body.appendChild(probe)
  const style = getComputedStyle(probe)
  const box = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  }
  probe.remove()
  return box
}

export function collectMetrics(): ScreenMetrics {
  const dpr = window.devicePixelRatio || 1
  const cssWidth = screen.width
  const cssHeight = screen.height
  const orientation = screen.orientation
  const visual = window.visualViewport

  let colorGamut = 'srgb'
  if (mq('(color-gamut: rec2020)')) colorGamut = 'rec2020'
  else if (mq('(color-gamut: p3)')) colorGamut = 'p3'

  return {
    cssWidth,
    cssHeight,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    dpr,
    physicalWidth: Math.round(cssWidth * dpr),
    physicalHeight: Math.round(cssHeight * dpr),
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    visualScale: visual ? visual.scale : null,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    orientationType: orientation?.type ?? (cssWidth >= cssHeight ? 'landscape' : 'portrait'),
    orientationAngle: orientation?.angle ?? null,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    colorScheme: mq('(prefers-color-scheme: dark)') ? 'dark' : 'light',
    reducedMotion: mq('(prefers-reduced-motion: reduce)'),
    contrast: mq('(prefers-contrast: more)')
      ? 'more'
      : mq('(prefers-contrast: less)')
        ? 'less'
        : 'no-preference',
    colorGamut,
    hdr: mq('(dynamic-range: high)') || mq('(video-dynamic-range: high)'),
    aspectRatio: aspectLabel(cssWidth, cssHeight),
    safeArea: readSafeArea(),
    isExtended: 'isExtended' in screen ? Boolean((screen as Screen & { isExtended?: boolean }).isExtended) : null,
  }
}

export function metricsSummary(m: ScreenMetrics): string {
  return [
    `CSS: ${m.cssWidth}×${m.cssHeight}`,
    `Physical(est): ${m.physicalWidth}×${m.physicalHeight}`,
    `DPR: ${m.dpr}`,
    `Viewport: ${m.innerWidth}×${m.innerHeight}`,
    `Orientation: ${m.orientationType}`,
    `Touch points: ${m.maxTouchPoints}`,
    `Gamut: ${m.colorGamut}`,
    `HDR: ${m.hdr ? 'yes' : 'no'}`,
    `Color scheme: ${m.colorScheme}`,
  ].join('\n')
}

export function onMetricsChange(callback: () => void): () => void {
  const run = () => callback()
  window.addEventListener('resize', run)
  window.addEventListener('orientationchange', run)
  window.visualViewport?.addEventListener('resize', run)
  window.visualViewport?.addEventListener('scroll', run)

  const medias = [
    '(prefers-color-scheme: dark)',
    '(prefers-reduced-motion: reduce)',
    '(prefers-contrast: more)',
    '(color-gamut: p3)',
    '(dynamic-range: high)',
  ].map((q) => {
    const mql = window.matchMedia(q)
    mql.addEventListener('change', run)
    return mql
  })

  return () => {
    window.removeEventListener('resize', run)
    window.removeEventListener('orientationchange', run)
    window.visualViewport?.removeEventListener('resize', run)
    window.visualViewport?.removeEventListener('scroll', run)
    medias.forEach((mql) => mql.removeEventListener('change', run))
  }
}
